import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SwipeLeftIcon from "@mui/icons-material/SwipeLeft";
import { ProfileSectionTitle } from "../profile/ProfileSectionTitle";
import {
  MiContenidoCollectionContentTableSkeleton,
  PublicReportContentTableSkeleton,
  BrandMentionsTableSkeleton,
  BrandPresenceTableSkeleton,
} from "./CollectionContentTableSkeletons";
import { collectionContenidoTableSx } from "./collectionContentTableLayout";
import {
  COLLECTION_FECHA_COLUMN_WIDTH,
  COLLECTION_TIPO_COLUMN_WIDTH,
  COLLECTION_IMAGEN_COLUMN_WIDTH,
  COLLECTION_METRIC_NARROW_COLUMN_WIDTH,
  COLLECTION_DETALLES_COLUMN_WIDTH,
} from "./collectionContentTableConstants";

const TABLE_ROWS = 6;

/** MUI Skeleton `text` defaults to scaleY(0.6) — disable so height matches loaded typography. */
const skeletonLineSx = { transform: "none" } as const;

/** Fixed heights matching PublicSponsorLink header slots (avoids dual-round layout jump). */
const HEADER_LOGO_SIZE = 80;
const HEADER_H2_SKELETON_HEIGHT = 28;
const HEADER_PERIODO_SKELETON_HEIGHT = 16;
const HEADER_SUMMARY_LINE_HEIGHT = 18;
const HEADER_SUMMARY_MIN_HEIGHT = HEADER_SUMMARY_LINE_HEIGHT + 2;

/** Matches MUI FormControlLabel + Checkbox row height in filter sections. */
const FILTER_CHECKBOX_ROW_HEIGHT = 42;

function FilterCheckboxSkeleton({ labelWidth }: { labelWidth: number }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ height: FILTER_CHECKBOX_ROW_HEIGHT }}
    >
      <Skeleton
        variant="rounded"
        width={18}
        height={18}
        sx={{ borderRadius: 0.5, flexShrink: 0 }}
      />
      <Skeleton
        variant="text"
        width={labelWidth}
        height={20}
        sx={skeletonLineSx}
      />
    </Stack>
  );
}

function SectionStack({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={3}>
      <ProfileSectionTitle title={title} action={action} />
      {children}
    </Stack>
  );
}

/**
 * Must match PublicSponsorLink header shell (logo · h1/PDF · H2 · Periodo · summary)
 * so the gate/route skeleton does not remount as a different layout on the page.
 */
export function PublicReportHeaderSkeleton({
  isBrandReport = false,
}: {
  /** Brand URL (`/brands/:id`) — h1 is "Reporte de Marca"; otherwise "Reporte de Contenidos". */
  isBrandReport?: boolean;
}) {
  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          mt: { xs: 2, sm: 4 },
        }}
      >
        <Box
          sx={{
            width: HEADER_LOGO_SIZE,
            height: HEADER_LOGO_SIZE,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <Skeleton
            variant="rectangular"
            width={HEADER_LOGO_SIZE}
            height={HEADER_LOGO_SIZE}
            sx={{ borderRadius: 1 }}
          />
        </Box>
        <Button
          variant="outlined"
          disabled
          startIcon={<FileDownloadOutlinedIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{ flexShrink: 0 }}
        >
          Descargar
        </Button>
      </Box>
      <Typography variant="h1">
        {isBrandReport ? "Reporte de Marca" : "Reporte de Contenidos"}
      </Typography>
      <Box>
        <Skeleton
          variant="text"
          height={HEADER_H2_SKELETON_HEIGHT}
          sx={{
            ...skeletonLineSx,
            width: { xs: "85%", sm: 360 },
            maxWidth: "100%",
          }}
        />
        <Skeleton
          variant="text"
          height={HEADER_PERIODO_SKELETON_HEIGHT}
          sx={{
            ...skeletonLineSx,
            mt: 0.5,
            width: { xs: "85%", sm: 320 },
            maxWidth: "100%",
          }}
        />
      </Box>
      <Box sx={{ minHeight: HEADER_SUMMARY_MIN_HEIGHT }}>
        <Skeleton
          variant="text"
          width="90%"
          height={HEADER_SUMMARY_LINE_HEIGHT}
          sx={skeletonLineSx}
        />
      </Box>
    </Stack>
  );
}

export function PublicReportContenidoSectionSkeleton() {
  return (
    <SectionStack title="Contenido">
      <Card>
        <CardContent>
          <Box
            sx={{
              display: { xs: "flex", lg: "none" },
              alignItems: "center",
              gap: 1,
              mb: 2,
              color: "text.secondary",
            }}
          >
            <SwipeLeftIcon />
            <Skeleton variant="text" width={120} height={16} />
          </Box>
          <PublicReportContentTableSkeleton embedded />
        </CardContent>
      </Card>
    </SectionStack>
  );
}

export function GroupDetailFiltersBodySkeleton() {
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={3} divider={<Divider flexItem />}>
        {[
          [
            { title: 140, labels: [180, 180, 220, 210] },
            { title: 190, labels: [160, 180, 140] },
          ],
          [
            { title: 90, labels: [160, 160] },
            { title: 80, labels: [220, 180, 240] },
            { title: 90, labels: [190] },
          ],
        ].map((groups, categoryIndex) => (
          <Box key={categoryIndex}>
            <Skeleton
              variant="text"
              width={80}
              height={16}
              sx={{ ...skeletonLineSx, mb: 2 }}
            />
            <Grid container spacing={3}>
              {groups.map((group, groupIndex) => (
                <Grid
                  key={groupIndex}
                  size={{ xs: 12, md: groups.length === 3 ? 4 : 6 }}
                >
                  <Skeleton
                    variant="text"
                    width={group.title}
                    height={22}
                    sx={skeletonLineSx}
                  />
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={18}
                    sx={{ ...skeletonLineSx, mt: 0.5 }}
                  />
                  <Box
                    sx={{
                      mt: 1,
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        sm:
                          categoryIndex === 0
                            ? "repeat(2, minmax(0, 1fr))"
                            : "minmax(0, 1fr)",
                      },
                      columnGap: 2,
                      rowGap: 0.25,
                      justifyItems: "start",
                    }}
                  >
                    {group.labels.map((labelWidth, i) => (
                      <FilterCheckboxSkeleton key={i} labelWidth={labelWidth} />
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

export function GroupDetailFiltersSectionSkeleton() {
  return (
    <SectionStack title="Qué datos incluir">
      <GroupDetailFiltersBodySkeleton />
    </SectionStack>
  );
}

export function GroupDetailBrandLinksBodySkeleton() {
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      {[160, 120].map((labelWidth, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1.25,
            "&:not(:last-of-type)": {
              borderBottom: 1,
              borderColor: "divider",
            },
          }}
        >
          <Skeleton
            variant="rounded"
            width={32}
            height={32}
            sx={{ borderRadius: 1, flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton
              variant="text"
              width={labelWidth}
              height={20}
              sx={skeletonLineSx}
            />
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            {[52, 104, 80].map((width, i) => (
              <Skeleton key={i} variant="rounded" width={width} height={30} />
            ))}
          </Stack>
        </Box>
      ))}
    </Paper>
  );
}

export function GroupDetailBrandLinksSectionSkeleton() {
  return (
    <SectionStack title="Reportes">
      <GroupDetailBrandLinksBodySkeleton />
    </SectionStack>
  );
}

export function GroupDetailContenidoBodySkeleton({
  showAccountColumn = false,
}: {
  showAccountColumn?: boolean;
}) {
  return (
    <MiContenidoCollectionContentTableSkeleton
      rows={5}
      showAccountColumn={showAccountColumn}
    />
  );
}

export function GroupDetailContenidoSectionSkeleton({
  showAccountColumn = false,
}: {
  showAccountColumn?: boolean;
}) {
  return (
    <SectionStack title="Contenido">
      <GroupDetailContenidoBodySkeleton showAccountColumn={showAccountColumn} />
    </SectionStack>
  );
}

export function CollectionBrandIntegrationBodySkeleton() {
  return (
    <>
      <Card sx={{ mb: 1 }}>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <TableCell key={i}>
                      <Skeleton variant="text" width={90} height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: TABLE_ROWS }).map((_, row) => (
                  <TableRow key={row} sx={{ height: 80 }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="text" width={120} height={20} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="text" width={100} height={20} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={60} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={40} height={20} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Skeleton variant="text" width={200} height={32} />
            <Skeleton variant="rounded" width={180} height={40} />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <TableCell key={i}>
                      <Skeleton variant="text" width={90} height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 4 }).map((_, row) => (
                  <TableRow key={row} sx={{ height: 80 }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="text" width={120} height={20} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="text" width={100} height={20} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={60} height={20} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
}

export function CollectionBrandIntegrationSectionSkeleton({
  showAccountColumn = false,
  afterMentions,
}: {
  showAccountColumn?: boolean;
  /** Rendered after Menciones de marca and before Presencia de marca en RRSS. */
  afterMentions?: React.ReactNode;
}) {
  return (
    <Stack spacing={4}>
      <Stack spacing={3}>
        <ProfileSectionTitle title="Menciones de marca" />
        <Card sx={{ mb: 1 }}>
          <CardContent>
            <BrandMentionsTableSkeleton
              embedded
              showAccountColumn={showAccountColumn}
            />
          </CardContent>
        </Card>
      </Stack>

      {afterMentions}

      <Stack spacing={3}>
        <ProfileSectionTitle title="Presencia de marca en RRSS" />
        <Card>
          <CardContent>
            <BrandPresenceTableSkeleton
              embedded
              showAccountColumn={showAccountColumn}
            />
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
}

export function CollectionLivestreamsBodySkeleton() {
  return (
    <Card>
      <CardContent>
        <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
          <Table sx={collectionContenidoTableSx}>
            <TableHead>
              <TableRow>
                {[
                  { label: "Fecha", width: COLLECTION_FECHA_COLUMN_WIDTH },
                  { label: "Tipo", width: COLLECTION_TIPO_COLUMN_WIDTH },
                  { label: "Imagen", width: COLLECTION_IMAGEN_COLUMN_WIDTH },
                  { label: "Título" },
                  {
                    label: "Duración",
                    align: "right" as const,
                    width: Math.round(
                      COLLECTION_METRIC_NARROW_COLUMN_WIDTH * 1.1 * 1.1,
                    ),
                  },
                  {
                    label: "Espectadores",
                    align: "right" as const,
                    width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH,
                  },
                  {
                    label: "Peak",
                    align: "right" as const,
                    width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH,
                  },
                  {
                    label: "",
                    align: "right" as const,
                    width: COLLECTION_DETALLES_COLUMN_WIDTH,
                  },
                ].map((col) => (
                  <TableCell
                    key={col.label}
                    align={"align" in col ? col.align : "left"}
                    sx={
                      "width" in col && col.width
                        ? {
                            width: col.width,
                            minWidth: col.width,
                            maxWidth: col.width,
                          }
                        : undefined
                    }
                  >
                    <Skeleton
                      variant="text"
                      width={col.width ? col.width - 16 : 120}
                      height={24}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 3 }).map((_, row) => (
                <TableRow key={row} sx={{ height: 56 }}>
                  <TableCell sx={{ width: COLLECTION_FECHA_COLUMN_WIDTH }}>
                    <Skeleton variant="text" width={72} height={20} />
                  </TableCell>
                  <TableCell sx={{ width: COLLECTION_TIPO_COLUMN_WIDTH }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width={72} height={20} />
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ width: COLLECTION_IMAGEN_COLUMN_WIDTH }}>
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="85%" height={20} />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: Math.round(
                        COLLECTION_METRIC_NARROW_COLUMN_WIDTH * 1.1 * 1.1,
                      ),
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width={48}
                      height={20}
                      sx={{ ml: "auto" }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH }}
                  >
                    <Skeleton
                      variant="text"
                      width={56}
                      height={20}
                      sx={{ ml: "auto" }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH }}
                  >
                    <Skeleton
                      variant="text"
                      width={56}
                      height={20}
                      sx={{ ml: "auto" }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: COLLECTION_DETALLES_COLUMN_WIDTH }}
                  >
                    <Skeleton
                      variant="rounded"
                      width={48}
                      height={28}
                      sx={{ ml: "auto" }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export function CollectionLivestreamsSectionSkeleton() {
  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <ProfileSectionTitle title="Livestreams" />
        <Typography variant="body1" color="text.secondary">
          Métricas de transmisiones en vivo en YouTube.
        </Typography>
      </Stack>
      <CollectionLivestreamsBodySkeleton />
    </Stack>
  );
}

export function CollectionEpisodeRetentionSectionSkeleton() {
  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <ProfileSectionTitle title="Retención de episodios" />
        <Typography variant="body1" color="text.secondary">
          Retención de audiencia YouTube al 25%, 50% y 75% de la duración.
        </Typography>
      </Stack>
      <CollectionLivestreamsBodySkeleton />
    </Stack>
  );
}

function LivestreamSummaryTableSkeleton({
  includeChatColumns = true,
}: {
  includeChatColumns?: boolean;
}) {
  const columnCount = includeChatColumns ? 8 : 6;

  return (
    <TableContainer
      sx={{
        overflowX: "auto",
        mb: 2,
        px: 2,
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            {Array.from({ length: columnCount }).map((_, index) => (
              <TableCell key={index} align={index === 0 ? "left" : "right"}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: index === 0 ? "flex-start" : "flex-end",
                  }}
                >
                  <Skeleton
                    variant="text"
                    width={index === 0 ? 72 : 108}
                    height={20}
                  />
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={{ height: 52 }}>
            {Array.from({ length: columnCount }).map((_, index) => (
              <TableCell key={index} align={index === 0 ? "left" : "right"}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: index === 0 ? "flex-start" : "flex-end",
                  }}
                >
                  <Skeleton
                    variant="text"
                    width={index === 0 ? 48 : 64}
                    height={20}
                  />
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PlatformMetricsTableSkeleton({
  contentRows = 2,
  showTotalRow = true,
}: {
  contentRows?: number;
  showTotalRow?: boolean;
}) {
  const metricColumns = ["Cantidad", "Vistas", "Likes", "Comentarios"];

  return (
    <TableContainer
      sx={{
        overflowX: "auto",
        mb: 2,
        px: 2,
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Tipo</TableCell>
            {metricColumns.map((label) => (
              <TableCell key={label} align="right">
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: contentRows }).map((_, row) => (
            <TableRow key={row} sx={{ height: 52 }}>
              <TableCell>
                <Skeleton variant="text" width={96} height={20} />
              </TableCell>
              {metricColumns.map((label) => (
                <TableCell key={label} align="right">
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="text" width={56} height={20} />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          ))}
          {showTotalRow ? (
            <TableRow sx={{ height: 52 }}>
              <TableCell>
                <Skeleton variant="text" width={112} height={20} />
              </TableCell>
              {metricColumns.map((label) => (
                <TableCell key={`total-${label}`} align="right">
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="text" width={64} height={20} />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PlatformMetricsBlockSkeleton({
  nameWidth,
  contentRows = 2,
}: {
  nameWidth: number;
  contentRows?: number;
}) {
  return (
    <Box>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          alignItems: "center",
          gap: 1,
          mb: 2,
          color: "text.secondary",
        }}
      >
        <SwipeLeftIcon />
        <Typography variant="caption">Desliza para ver más</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton
          variant="text"
          width={nameWidth}
          sx={{
            fontSize: "1.5rem",
            fontWeight: 700,
            lineHeight: 1.334,
            display: "block",
            ...skeletonLineSx,
          }}
        />
      </Box>
      <PlatformMetricsTableSkeleton contentRows={contentRows} />
    </Box>
  );
}

export function CollectionPlatformStatsBodySkeleton({
  showLivestreamSummary = false,
  includeLivestreamChat = true,
}: {
  showLivestreamSummary?: boolean;
  includeLivestreamChat?: boolean;
}) {
  return (
    <Card>
      <CardContent>
        <PlatformMetricsBlockSkeleton nameWidth={88} contentRows={2} />
        <Divider
          sx={{
            my: 3,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "none",
          }}
        />
        <PlatformMetricsBlockSkeleton nameWidth={104} contentRows={1} />
        <Divider
          sx={{
            my: 3,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "none",
          }}
        />
        <PlatformMetricsBlockSkeleton nameWidth={72} contentRows={1} />

        {showLivestreamSummary ? (
          <>
            <Divider
              sx={{
                my: 3,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            />
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton
                  variant="text"
                  width={180}
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    lineHeight: 1.334,
                    display: "block",
                    ...skeletonLineSx,
                  }}
                />
              </Box>
              <LivestreamSummaryTableSkeleton
                includeChatColumns={includeLivestreamChat}
              />
            </Box>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function CollectionPlatformStatsSectionSkeleton({
  showLivestreamSummary = false,
  includeLivestreamChat = true,
}: {
  showLivestreamSummary?: boolean;
  includeLivestreamChat?: boolean;
}) {
  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <ProfileSectionTitle title="Métricas por plataforma" />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: (theme) => theme.typography.fontFamily }}
        >
          Estas métricas corresponden a todas las piezas de contenido asociado a
          este reporte. Métricas como Views, Likes, Comments, Shares, Saves,
          Reach, Interactions, son acumuladas de todas las piezas de contenido
          por plataforma.
        </Typography>
      </Stack>

      <CollectionPlatformStatsBodySkeleton
        showLivestreamSummary={showLivestreamSummary}
        includeLivestreamChat={includeLivestreamChat}
      />
    </Stack>
  );
}

export type PublicReportPageSkeletonProps = {
  showBrandSections?: boolean;
  showLivestreams?: boolean;
  /** When true (brand report URL), header h1 matches page: "Reporte de Marca". */
  isBrandReport?: boolean;
};

export function PublicReportPageSkeleton({
  showBrandSections = true,
  showLivestreams = true,
  isBrandReport = false,
}: PublicReportPageSkeletonProps) {
  return (
    <Stack spacing={4}>
      <PublicReportHeaderSkeleton isBrandReport={isBrandReport} />
      <PublicReportContenidoSectionSkeleton />
      {showBrandSections ? <CollectionBrandIntegrationSectionSkeleton /> : null}
      {showLivestreams ? <CollectionLivestreamsSectionSkeleton /> : null}
      <CollectionPlatformStatsSectionSkeleton
        showLivestreamSummary={showLivestreams}
      />
    </Stack>
  );
}

export type GroupDetailPageSkeletonProps = {
  showBrandLinks?: boolean;
  showBrandSections?: boolean;
  showLivestreams?: boolean;
};

export function GroupDetailPageSkeleton({
  showBrandLinks = true,
  showBrandSections = true,
  showLivestreams = true,
}: GroupDetailPageSkeletonProps) {
  return (
    <GroupDetailMiContenidoLoadingShell
      showBrandSections={showBrandSections}
      showLivestreams={showLivestreams}
      showBrandLinks={showBrandLinks}
    />
  );
}

export type GroupDetailMiContenidoLoadingShellProps = {
  showBrandLinks?: boolean;
  showBrandSections?: boolean;
  showLivestreams?: boolean;
  showAccountColumn?: boolean;
};

/** Stable section tree for GroupDetail first paint — real titles, body skeletons only. */
export function GroupDetailMiContenidoLoadingShell({
  showBrandLinks = true,
  showBrandSections = true,
  showLivestreams = true,
  showAccountColumn = false,
}: GroupDetailMiContenidoLoadingShellProps) {
  return (
    <Stack spacing={4}>
      {showBrandLinks ? (
        <Stack spacing={3}>
          <ProfileSectionTitle title="Reportes" />
          <GroupDetailBrandLinksBodySkeleton />
        </Stack>
      ) : null}

      <Stack spacing={3}>
        <ProfileSectionTitle title="Qué datos incluir" />
        <GroupDetailFiltersBodySkeleton />
      </Stack>

      <Stack spacing={3}>
        <ProfileSectionTitle title="Contenido" />
        <GroupDetailContenidoBodySkeleton
          showAccountColumn={showAccountColumn}
        />
      </Stack>

      {showBrandSections ? (
        <CollectionBrandIntegrationSectionSkeleton
          showAccountColumn={showAccountColumn}
        />
      ) : null}

      {showLivestreams ? (
        <Stack spacing={3}>
          <Stack spacing={1}>
            <ProfileSectionTitle title="Livestreams" />
            <Typography variant="body1" color="text.secondary">
              Métricas de transmisiones en vivo en YouTube.
            </Typography>
          </Stack>
          <CollectionLivestreamsBodySkeleton />
        </Stack>
      ) : null}

      <Stack spacing={3}>
        <Stack spacing={1}>
          <ProfileSectionTitle title="Métricas por plataforma" />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: (theme) => theme.typography.fontFamily }}
          >
            Estas métricas corresponden a todas las piezas de contenido asociado
            a este reporte. Métricas como Views, Likes, Comments, Shares, Saves,
            Reach, Interactions, son acumuladas de todas las piezas de contenido
            por plataforma.
          </Typography>
        </Stack>
        <CollectionPlatformStatsBodySkeleton
          showLivestreamSummary={showLivestreams}
        />
      </Stack>
    </Stack>
  );
}

export function PublicReportRouteSkeletonContainer({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Container
      maxWidth="lg"
      sx={{
        bgcolor: "background.default",
        px: { xs: 2, sm: 3, md: 4 },
        minHeight: "100vh",
        overflowX: "hidden",
        width: "100%",
        maxWidth: { xs: "100vw", sm: "lg" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          pb: { xs: 3, sm: 4 },
          pt: { xs: 2, sm: 4 },
        }}
      >
        {children}
      </Box>
    </Container>
  );
}
